/**
 * mm-reviews.js
 * -------------
 * The custom Judge.me reviews widget that fills #judgeme_product_reviews
 * (rendered by sections/mmreviews.liquid, which contains no JS of its own).
 *
 * Extracted verbatim from assets/mix-match.js so it can run on product
 * templates that do NOT instantiate MixMatch. The MixMatch constructor
 * returns early when there is no `mix-match-config-<id>` script tag, which
 * the static-title templates (product.mix-match-sale / -style) deliberately
 * don't emit — so on those pages init() never ran and the widget stayed
 * blank while the server-rendered star badge above it still showed.
 *
 * Two entry points, and they cooperate:
 *   - Auto-init on DOMContentLoaded, reading the product id from the
 *     container's own data-id. This is what static-title templates rely on.
 *   - window.MMReviews.render(productId) — called by mix-match.js on initial
 *     load and again after a mix-match product switch.
 *
 * Double-fetch is prevented by tracking the last rendered product id: whoever
 * gets there first wins, and a repeat call for the same product is a no-op.
 * A product switch passes a different id, so it always re-renders.
 *
 * Behaviour is intentionally identical to the original: same API call, same
 * markup, same classes, same lightbox. All styling already lives in
 * mix-match.css.
 */
(function () {
  'use strict';

  var state = {
    productId: null,
    shopDomain: null,
    page: 1,
    container: null,
    images: [],
    renderedFor: null
  };

  function getContainer() {
    return document.getElementById('judgeme_product_reviews');
  }

  function getShopDomain() {
    if (!window.jdgm) return null;
    return typeof window.jdgm.shopDomain === 'function'
      ? window.jdgm.shopDomain()
      : (window.jdgmSettings && window.jdgmSettings.shop_domain);
  }

  /**
   * Render reviews for a product. Repeat calls for the same product are
   * ignored unless `force` is true.
   */
  function render(productId, force) {
    var container = getContainer();
    if (!container || !window.jdgm) return;
    if (!productId) return;
    if (!force && state.renderedFor === String(productId)) return;

    var shopDomain = getShopDomain();
    if (!shopDomain) return;

    state.productId = productId;
    state.shopDomain = shopDomain;
    state.page = 1;
    state.container = container;
    state.renderedFor = String(productId);

    fetchAndRender(productId, shopDomain, container, 1, true);
  }

  function fetchAndRender(productId, shopDomain, container, page, replace) {
    var reviewsSection = document.querySelector('[data-mm-reviews-section]');
    var perPage = parseInt(reviewsSection && reviewsSection.dataset.reviewsPerPage) || 9;
    var apiUrl = 'https://judge.me/reviews/reviews_for_widget?url=' + shopDomain +
      '&shop_domain=' + shopDomain +
      '&platform=shopify&page=' + page + '&per_page=' + perPage +
      '&sort_key=rating&sort_dir=desc' +
      '&product_id=' + productId;

    fetch(apiUrl)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var rating = parseFloat(data.average_rating) || 0;
        var count = parseInt(data.number_of_reviews) || 0;

        // Build stars
        var fullStars = Math.floor(rating);
        var halfStar = (rating - fullStars) >= 0.5;
        var starsHtml = '';
        for (var i = 0; i < 5; i++) {
          if (i < fullStars) starsHtml += '★';
          else if (i === fullStars && halfStar) starsHtml += '★';
          else starsHtml += '☆';
        }

        // Build individual review cards
        var reviewsHtml = '';
        if (data.reviews && data.reviews.length) {
          data.reviews.forEach(function (rev) {
            var rounded = Math.round(parseFloat(rev.rating) || 0);
            var revStars = '★'.repeat(rounded) + '☆'.repeat(5 - rounded);
            var date = new Date(rev.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
            var verified = rev.verified_buyer ? '<span class="mm-review__verified">✓ Verified Buyer</span>' : '';
            var name = rev.reviewer_name || 'Anonymous';
            var revImagesHtml = '';
            if (rev.pictures_urls && rev.pictures_urls.length) {
              var revImgs = rev.pictures_urls.map(function (pic) {
                var thumb = pic.huge || pic.original || pic.compact;
                var full = pic.original || pic.huge || pic.compact;
                return '<img class="mm-review__inline-img" src="' + thumb + '" alt="Review photo" loading="lazy" data-full="' + full + '">';
              }).join('');
              revImagesHtml = '<div class="mm-review__images">' + revImgs + '</div>';
            }
            reviewsHtml += '<div class="mm-review">' +
              '<div class="mm-review__header">' +
                '<span class="mm-review__stars">' + revStars + '</span>' +
                '<span class="mm-review__date">' + date + '</span>' +
              '</div>' +
              '<div class="mm-review__title">' + (rev.title || '') + '</div>' +
              '<div class="mm-review__body">' + (rev.body_html || '') + '</div>' +
              revImagesHtml +
              '<div class="mm-review__author">' + name + ' ' + verified + '</div>' +
            '</div>';
          });
        }

        var totalPages = data.pagination ? Math.ceil(count / perPage) : 1;
        var hasMore = page < totalPages;

        if (replace) {
          var summaryHtml = '<div class="mm-reviews-summary">' +
            '<span class="mm-reviews-summary__stars">' + starsHtml + '</span> ' +
            '<span class="mm-reviews-summary__rating">' + rating.toFixed(1) + '</span> ' +
            '<span class="mm-reviews-summary__count">(' + count + ' review' + (count !== 1 ? 's' : '') + ')</span>' +
          '</div>';

          var galleryHtml = '';
          var allImages = [];
          if (data.photo_gallery && Array.isArray(data.photo_gallery)) {
            data.photo_gallery.forEach(function (item) {
              if (item.pictures_urls && item.pictures_urls.length) {
                item.pictures_urls.forEach(function (pic) {
                  var thumb = pic.huge || pic.original || pic.compact;
                  var full = pic.original || pic.huge || pic.compact;
                  if (thumb && full) allImages.push({ thumb: thumb, full: full });
                });
              }
            });
          }
          if (data.reviews) {
            data.reviews.forEach(function (rev) {
              if (rev.pictures_urls && rev.pictures_urls.length) {
                rev.pictures_urls.forEach(function (pic) {
                  var thumb = pic.huge || pic.original || pic.compact;
                  var full = pic.original || pic.huge || pic.compact;
                  if (thumb && full) allImages.push({ thumb: thumb, full: full });
                });
              }
            });
          }

          if (allImages.length > 0) {
            var thumbs = allImages.map(function (img, i) {
              return '<div class="mm-review-gallery__thumb" data-mm-gallery-index="' + i + '">' +
                '<img src="' + img.thumb + '" alt="Customer review photo" loading="lazy">' +
              '</div>';
            }).join('');
            galleryHtml = '<div class="mm-review-gallery">' +
              '<div class="mm-review-gallery__track">' + thumbs + '</div>' +
            '</div>';
            state.images = allImages.map(function (img) { return img.full; });
          }

          container.innerHTML =
            '<div class="mm-reviews-widget">' +
              summaryHtml +
              galleryHtml +
              '<div class="mm-reviews-list">' + reviewsHtml + '</div>' +
              (hasMore ? '<div class="mm-reviews-load-more"><button type="button" class="mm-reviews-load-more__btn">Load More Reviews</button></div>' : '') +
            '</div>';

          // Inline review image clicks (delegated).
          // Bound once per full rebuild, matching the original.
          container.addEventListener('click', function (e) {
            var inlineImg = e.target.closest('.mm-review__inline-img');
            if (!inlineImg) return;
            var fullSrc = inlineImg.dataset.full;
            if (!fullSrc) return;
            state.images = [fullSrc];
            var review = inlineImg.closest('.mm-review');
            if (review) {
              var allInline = Array.prototype.slice.call(review.querySelectorAll('.mm-review__inline-img'));
              state.images = allInline.map(function (img) { return img.dataset.full; });
              var idx = allInline.indexOf(inlineImg);
              openLightbox(idx >= 0 ? idx : 0);
            } else {
              openLightbox(0);
            }
          });

          if (allImages.length > 0) {
            container.querySelectorAll('.mm-review-gallery__thumb').forEach(function (thumb) {
              thumb.addEventListener('click', function () {
                openLightbox(parseInt(thumb.dataset.mmGalleryIndex));
              });
            });

            var gallery = container.querySelector('.mm-review-gallery');
            var track = container.querySelector('.mm-review-gallery__track');
            if (gallery && track) {
              var checkOverflow = function () {
                var hasOverflow = track.scrollWidth > track.clientWidth;
                var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
                gallery.classList.toggle('mm-review-gallery--has-overflow', hasOverflow && !atEnd);
              };
              checkOverflow();
              track.addEventListener('scroll', checkOverflow);
              window.addEventListener('resize', checkOverflow);
            }
          }
        } else {
          var list = container.querySelector('.mm-reviews-list');
          if (list) list.insertAdjacentHTML('beforeend', reviewsHtml);
          var loadMoreWrap = container.querySelector('.mm-reviews-load-more');
          if (loadMoreWrap) {
            if (hasMore) {
              loadMoreWrap.querySelector('.mm-reviews-load-more__btn').disabled = false;
              loadMoreWrap.querySelector('.mm-reviews-load-more__btn').textContent = 'Load More Reviews';
            } else {
              loadMoreWrap.remove();
            }
          }
        }

        var loadMoreBtn = container.querySelector('.mm-reviews-load-more__btn');
        if (loadMoreBtn) {
          loadMoreBtn.addEventListener('click', function () {
            loadMoreBtn.disabled = true;
            loadMoreBtn.textContent = 'Loading...';
            state.page = page + 1;
            fetchAndRender(productId, shopDomain, container, state.page, false);
          });
        }

        // Update the preview badge count at the top of the page.
        // The original scoped this to the MixMatch sectionContainer; there is
        // only ever one badge per page, so scope to the product section when
        // present and fall back to the document.
        var scope = document.querySelector('[data-section-type="product-template"]') || document;
        var badgeWidget = scope.querySelector('.jdgm-preview-badge');
        if (badgeWidget) {
          var badgeCount = badgeWidget.querySelector('.jdgm-prev-badge__text');
          if (badgeCount) {
            badgeCount.textContent = count + ' review' + (count !== 1 ? 's' : '');
          }
        }
      })
      .catch(function (e) {
        console.warn('mm-reviews: could not fetch reviews', e);
      });
  }

  // --- Review image lightbox ---
  function openLightbox(index) {
    var images = state.images;
    if (!images || !images.length) return;

    var existing = document.querySelector('.mm-lightbox');
    if (existing) existing.remove();

    var currentIndex = index;

    var lightbox = document.createElement('div');
    lightbox.className = 'mm-lightbox';
    lightbox.innerHTML =
      '<div class="mm-lightbox__overlay"></div>' +
      '<div class="mm-lightbox__content">' +
        '<button type="button" class="mm-lightbox__close">✕</button>' +
        '<button type="button" class="mm-lightbox__prev">‹</button>' +
        '<img class="mm-lightbox__img" src="' + images[currentIndex] + '" alt="Review photo">' +
        '<button type="button" class="mm-lightbox__next">›</button>' +
        '<div class="mm-lightbox__counter">' + (currentIndex + 1) + ' / ' + images.length + '</div>' +
      '</div>';

    document.body.appendChild(lightbox);

    var img = lightbox.querySelector('.mm-lightbox__img');
    var counter = lightbox.querySelector('.mm-lightbox__counter');

    var updateImage = function () {
      img.src = images[currentIndex];
      counter.textContent = (currentIndex + 1) + ' / ' + images.length;
    };

    lightbox.querySelector('.mm-lightbox__close').addEventListener('click', function () { lightbox.remove(); });
    lightbox.querySelector('.mm-lightbox__overlay').addEventListener('click', function () { lightbox.remove(); });
    lightbox.querySelector('.mm-lightbox__prev').addEventListener('click', function () {
      currentIndex = (currentIndex - 1 + images.length) % images.length;
      updateImage();
    });
    lightbox.querySelector('.mm-lightbox__next').addEventListener('click', function () {
      currentIndex = (currentIndex + 1) % images.length;
      updateImage();
    });

    var keyHandler = function (e) {
      if (e.key === 'Escape') { lightbox.remove(); document.removeEventListener('keydown', keyHandler); }
      if (e.key === 'ArrowLeft') { currentIndex = (currentIndex - 1 + images.length) % images.length; updateImage(); }
      if (e.key === 'ArrowRight') { currentIndex = (currentIndex + 1) % images.length; updateImage(); }
    };
    document.addEventListener('keydown', keyHandler);
  }

  /**
   * Auto-init. Reads the product id straight off the container that
   * sections/mmreviews.liquid renders. Judge.me's own script may still be
   * loading, so retry briefly before giving up.
   */
  function autoInit(attempt) {
    attempt = attempt || 0;
    var container = getContainer();
    if (!container) return;
    var productId = container.dataset.id;
    if (!productId) return;

    if (!window.jdgm || !getShopDomain()) {
      if (attempt < 20) setTimeout(function () { autoInit(attempt + 1); }, 300);
      return;
    }
    render(productId);
  }

  window.MMReviews = {
    render: render,
    open: openLightbox,
    autoInit: autoInit
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { autoInit(0); });
  } else {
    autoInit(0);
  }
})();
